import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useCompareRunsV1RunsCompareGet,
  useListJobsV1JobsGet,
  useListRunsV1RunsGet,
} from "../api/generated/qcsControlPlane";
import type { RunCompare, StatDelta } from "../api/generated/model";
import { RunChangesTable } from "../components/RunChangesTable";
import { StoreOverlayMap } from "../components/StoreOverlayMap";
import { SyncedCoverageMaps } from "../components/SyncedCoverageMaps";
import { SyncedStoreMaps } from "../components/SyncedStoreMaps";
import { isPlatform, platformFromKey, platformKeys } from "../api/platforms";
import {
  type Platform,
  type Run,
  flowLabel,
  fmtDateTime,
  fmtInt,
  platformLabel,
  statusTone,
} from "../api/labels";
import { AnimatedNumber } from "../lib/motion";
import { Badge, Button, EmptyState, ErrorState, PageHeader, Panel, Row, Skeleton, SkeletonRegion, Stack } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { NativeSelect as Select } from "../components/foundation";

// Two runs of the SAME flow, side by side.
//
// The page was built for one question: day vs night merchant sweep, is the set of stores
// serving at night different: and that comparison is still its richest: headline store
// counts (incl. the nightly-flag split), geographic coverage, and pan/zoom-synced maps,
// all fed by /v1/runs/compare (which is merchant-only, by schema and by 422).
//
// Every other flow gets the comparison that IS meaningful without maps: a counter diff.
// It needs no endpoint, the run list already carries each run's terminal `summary` (and
// live `progress`), which is the same JSONB the run monitor renders, so a flow joins this
// page by existing, not by being taught to it. That matters because the counters are how
// you tell two passes apart: which one wrote more rows, which burned more proxies, which
// gave up more pairs.

// A short label for a run option in the pickers. Accepts either the refined `Run`
// (from labels) or the raw `RunOut` embedded in a RunCompare side.
function runLabel(r: { started_at: string | null; created_at: string; status: string }): string {
  const when = fmtDateTime(r.started_at ?? r.created_at);
  return `${when} · ${r.status}`;
}

export function Compare() {
  // Deep-link support: /compare?platform=&run_a=&run_b= (e.g. from the job run history).
  const [searchParams] = useSearchParams();
  const [platform, setPlatform] = useState<Platform>(
    platformFromKey(searchParams.get("platform") ?? "") ?? platformKeys()[0],
  );
  // The flow being compared. Deep links (from a job's run history) carry it; otherwise the
  // merchant sweep, which is the only flow with the full map/diff comparison.
  const [flow, setFlow] = useState<string>(searchParams.get("flow") ?? "merchant_sweep");
  const [runA, setRunA] = useState(searchParams.get("run_a") ?? "");
  const [runB, setRunB] = useState(searchParams.get("run_b") ?? "");
  const [focus, setFocus] = useState<{ id: string; n: number } | null>(null);

  // A changes-table row's "locate" -> focus the store map on that store + scroll to it.
  const locate = (id: string) => {
    setFocus((f) => ({ id, n: (f?.n ?? 0) + 1 }));
    document.getElementById("store-maps")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const jobsQuery = useListJobsV1JobsGet();
  const runsQuery = useListRunsV1RunsGet(
    { limit: 200 },
    { query: { refetchInterval: 15000 } },
  );

  // Jobs of the selected flow on the selected platform -> the runs eligible for comparison.
  const jobIds = useMemo(() => {
    const jobs = jobsQuery.data ?? [];
    return new Set(jobs.filter((j) => j.platform === platform && j.flow === flow).map((j) => j.id));
  }, [jobsQuery.data, platform, flow]);

  // Flows that actually have a job on this platform. Derived rather than listed from the
  // Flow enum: an option that can only ever say "no runs" is a dead end the operator has to
  // discover by clicking it.
  const flowOptions = useMemo(() => {
    const jobs = jobsQuery.data ?? [];
    const present = new Set(jobs.filter((j) => j.platform === platform).map((j) => String(j.flow)));
    present.add(flow); // keep the current selection selectable even if its job was deleted
    return [...present].sort();
  }, [jobsQuery.data, platform, flow]);

  // The maps, the store diff and the /compare endpoint are all merchant-sweep only (the
  // endpoint 422s anything else), so everything below branches on this one flag.
  const isMerchant = flow === "merchant_sweep";

  const runs = useMemo(
    () => ((runsQuery.data ?? []) as Run[]).filter((r) => jobIds.has(r.job_id) && r.run_id),
    [runsQuery.data, jobIds],
  );

  // Default the pickers to the two most recent eligible runs whenever the platform
  // changes (or the list first loads) and the current selection is no longer valid.
  useEffect(() => {
    const ids = runs.map((r) => r.id);
    if (ids.length === 0) return; // still loading: don't clobber deep-linked selections
    if (!ids.includes(runA)) setRunA(ids[1] ?? ids[0] ?? "");
    if (!ids.includes(runB)) setRunB(ids[0] ?? "");
    // Depend on `runs` (not runsQuery.data): it also derives from jobsQuery.data, so
    // when jobs resolve AFTER runs the list goes empty->populated on a render that
    // wouldn't otherwise re-fire this effect: leaving the pickers blank until the
    // next refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, flow, runs]);

  const ready = Boolean(runA && runB && runA !== runB);
  const compare = useCompareRunsV1RunsCompareGet(
    { run_a: runA, run_b: runB },
    // Never fired for a non-merchant pair: the endpoint resolves both ids through a
    // merchant-only guard and 422s, which would render as a failed comparison on a page
    // that is in fact comparing them fine from the run rows it already has.
    { query: { enabled: ready && isMerchant } },
  );

  const data = compare.data;
  const runById = useMemo(() => new Map(runs.map((r) => [r.id, r])), [runs]);

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Run comparison"
        description={
          isMerchant
            ? "Day vs night merchant sweep · stores serving · coverage · maps"
            : `${flowLabel(flow)} · counter diff between two runs`
        }
      />

      {/* pickers */}
      <Panel className="mb-3.5">
        <Panel.Body>
          <Row gap="lg" align="end" wrap>
            <Field label="Platform">
              <Select
                className="mono"
                style={{ width: "auto", padding: "7px 10px", fontSize: 11.5 }}
                value={platform}
                onChange={(e) => {
                  const picked = e.currentTarget.value;
                  if (isPlatform(picked)) setPlatform(picked);
                }}
              >
                {platformKeys().map((p) => (
                  <option key={p} value={p}>
                    {platformLabel(p)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Flow">
              <Select
                className="mono"
                style={{ width: "auto", padding: "7px 10px", fontSize: 11.5 }}
                value={flow}
                onChange={(e) => setFlow(e.currentTarget.value)}
              >
                {flowOptions.map((f) => (
                  <option key={f} value={f}>
                    {flowLabel(f)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Run A (baseline)">
              <RunPicker runs={runs} value={runA} onChange={setRunA} />
            </Field>
            <Button
              variant="outline"
              size="sm"
              icon
              className="mb-1"
              onClick={() => {
                setRunA(runB);
                setRunB(runA);
              }}
              disabled={!runA || !runB}
              title="Swap Run A and Run B"
            >
              &#x21C4;
            </Button>
            <Field label="Run B (compared)">
              <RunPicker runs={runs} value={runB} onChange={setRunB} />
            </Field>
          </Row>
          {runs.length < 2 && !runsQuery.isLoading && (
            <div className="muted mt-2.5" style={{ fontSize: 12 }}>
              Need at least two {flowLabel(flow).toLowerCase()} runs on {platformLabel(platform)} to
              compare.
            </div>
          )}
          {runA && runB && runA === runB && (
            <div className="muted mt-2.5" style={{ fontSize: 12 }}>
              Pick two different runs.
            </div>
          )}
        </Panel.Body>
      </Panel>

      {/* The counter diff. Rendered for EVERY flow, merchant included: it is the only
          comparison that does not depend on an endpoint, so it is also the one that still
          answers when the merchant stats read misses. */}
      {ready && (
        <CountersTable
          a={runById.get(runA)}
          b={runById.get(runB)}
          labelA={runById.get(runA) ? runLabel(runById.get(runA)!) : "A"}
          labelB={runById.get(runB) ? runLabel(runById.get(runB)!) : "B"}
        />
      )}
      {ready && <div className="h-3.5" />}

      {compare.isError && (
        <Panel className="mb-3.5">
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load the comparison"
              message={(compare.error as Error)?.message}
              onRetry={() => compare.refetch()}
            />
          </Panel.Body>
        </Panel>
      )}

      {ready && isMerchant && compare.isLoading && !compare.data && (
        <Panel className="mb-3.5">
          <Panel.Body tight>
            <SkeletonRegion label="Loading comparison">
              <Stack gap="sm" className="p-4">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      )}

      {ready && isMerchant && !compare.isLoading && !compare.isError && data == null && (
        <Panel className="mb-3.5">
          <Panel.Body>
            <EmptyState compact title="No comparison data for this run pair" />
          </Panel.Body>
        </Panel>
      )}

      {ready && data && (
        <>
          <Headline data={data} />
          <MetricsTable
            data={data}
            labelA={runLabel(runById.get(runA) ?? data.run_a.run)}
            labelB={runLabel(runById.get(runB) ?? data.run_b.run)}
          />
          <div className="h-3.5" />
          <RunChangesTable runIdA={runA} runIdB={runB} onLocate={locate} />
          {/* Key the map panels on the run pair so switching runs remounts them,
              re-fitting the viewport to the new extent and clearing any stale
              store selection (the maps otherwise persist their one-time fit). */}
          <SyncedCoverageMaps
            key={`cov-${runA}-${runB}`}
            runIdA={runA}
            runIdB={runB}
            labelA={`A · ${runLabel(runById.get(runA) ?? data.run_a.run)}`}
            labelB={`B · ${runLabel(runById.get(runB) ?? data.run_b.run)}`}
          />
          <div id="store-maps">
            <SyncedStoreMaps
              key={`store-${runA}-${runB}`}
              runIdA={runA}
              runIdB={runB}
              labelA={`A · ${runLabel(runById.get(runA) ?? data.run_a.run)}`}
              labelB={`B · ${runLabel(runById.get(runB) ?? data.run_b.run)}`}
              focus={focus}
            />
          </div>
          <StoreOverlayMap
            key={`overlay-${runA}-${runB}`}
            runIdA={runA}
            runIdB={runB}
            labelA={`A · ${runLabel(runById.get(runA) ?? data.run_a.run)}`}
            labelB={`B · ${runLabel(runById.get(runB) ?? data.run_b.run)}`}
          />
        </>
      )}
    </>
  );
}

// The single answer the page exists to give: how many more/fewer stores serve in B.
function Headline({ data }: { data: RunCompare }) {
  if (data.delta == null) {
    return (
      <Panel className="mb-3.5">
        <Panel.Body>
          <span className="muted" style={{ fontSize: 12 }}>
            Stats are unavailable for {data.run_a.stats == null ? "run A" : "run B"}
            {data.run_a.stats == null && data.run_b.stats == null ? " and run B" : ""}, so the
            runs cannot be compared.
          </span>
        </Panel.Body>
      </Panel>
    );
  }
  const d = data.delta.stores;
  const a = data.run_a.stats?.stores ?? 0;
  const b = data.run_b.stats?.stores ?? 0;
  const same = d.abs === 0;
  const tone = same ? "idle" : d.abs > 0 ? "ok" : "crit";
  return (
    <Panel className="mb-3.5">
      <Panel.Body>
        <Row gap="lg" align="baseline" wrap>
          <Stack gap="xs">
            <span className="eyebrow">STORES SERVING</span>
            <span className="num" style={{ fontSize: 30 }}>
              <AnimatedNumber value={a} format={fmtInt} />{" "}
              <span className="faint">&#x2192;</span>{" "}
              <AnimatedNumber value={b} format={fmtInt} />
            </span>
          </Stack>
          <Badge variant={toneVariant(tone)} style={{ fontSize: 13, padding: "6px 12px" }}>
            {same ? "no change" : `${d.abs > 0 ? "+" : ""}${fmtInt(d.abs)} stores`}
            {d.pct != null && !same ? ` (${d.pct > 0 ? "+" : ""}${d.pct}%)` : ""}
          </Badge>
          <div className="flex-1" />
          <span className="muted" style={{ fontSize: 12, maxWidth: 360, lineHeight: 1.5 }}>
            {same
              ? "Both runs saw the same number of distinct stores serving - the day/night assumption is not supported by store count alone."
              : "Distinct stores that responded serviceable differs between the two runs - the day/night assumption holds for store count."}
          </span>
        </Row>
      </Panel.Body>
    </Panel>
  );
}

function MetricsTable({
  data,
  labelA,
  labelB,
}: {
  data: RunCompare;
  labelA: string;
  labelB: string;
}) {
  const a = data.run_a.stats;
  const b = data.run_b.stats;
  const rows: {
    key: string;
    delta: StatDelta | null;
    label: string;
    av: number | null;
    bv: number | null;
    pct?: boolean;
  }[] = [
    {
      key: "stores",
      delta: data.delta?.stores ?? null,
      label: "Distinct stores serving",
      av: a?.stores ?? null,
      bv: b?.stores ?? null,
    },
    {
      key: "nightly_stores",
      delta: data.delta?.nightly_stores ?? null,
      label: "Nightly-flagged stores",
      av: a?.nightly_stores ?? null,
      bv: b?.nightly_stores ?? null,
    },
    {
      key: "day_stores",
      delta: data.delta?.day_stores ?? null,
      label: "Day-only stores",
      av: a?.day_stores ?? null,
      bv: b?.day_stores ?? null,
    },
    {
      key: "serviceable_cells",
      delta: data.delta?.serviceable_cells ?? null,
      label: "Serviceable cells",
      av: a?.serviceable_cells ?? null,
      bv: b?.serviceable_cells ?? null,
    },
    {
      key: "probed_cells",
      delta: data.delta?.probed_cells ?? null,
      label: "Probed cells",
      av: a?.probed_cells ?? null,
      bv: b?.probed_cells ?? null,
    },
    {
      key: "coverage_pct",
      delta: data.delta?.coverage_pct ?? null,
      label: "Coverage %",
      av: a?.coverage_pct ?? null,
      bv: b?.coverage_pct ?? null,
      pct: true,
    },
  ];
  return (
    <Panel>
      <Panel.Head title="Metrics" />
      <Panel.Body tight>
        <table className="tbl" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Metric</th>
              <th style={{ textAlign: "right" }} title={labelA}>
                A
              </th>
              <th style={{ textAlign: "right" }} title={labelB}>
                B
              </th>
              <th style={{ textAlign: "right" }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  {r.av == null ? "—" : r.pct ? `${r.av}%` : fmtInt(r.av)}
                </td>
                <td className="num" style={{ textAlign: "right" }}>
                  {r.bv == null ? "—" : r.pct ? `${r.bv}%` : fmtInt(r.bv)}
                </td>
                <td style={{ textAlign: "right" }}>
                  <Delta d={r.delta} pct={r.pct} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="muted" style={{ fontSize: 11, padding: "8px 4px 2px", lineHeight: 1.5 }}>
          A = {labelA} · B = {labelB}. Stores = distinct store_ids that responded serviceable in the
          run (per-run sightings, not the rolling-window dimension). Nightly = stores carrying the
          platform&apos;s <code>is_nightly_store</code> flag.
        </div>
      </Panel.Body>
    </Panel>
  );
}

// Counters a reader looks at first, in the order they answer "what did these two runs do".
// Everything else follows alphabetically -- a curated list that HID an unlisted counter
// would be worse than no list, since the one you need is usually the new one.
const COUNTER_ORDER = [
  "rows_emitted",
  "store_sku_total",
  "pairs_total",
  "pairs_resolved",
  "pairs_absent",
  "pairs_changed",
  "pairs_lost",
  "pairs_restored",
  "store_sku_giveup",
  "store_sku_unresolved_id",
  "stores_total",
  "stores_processed",
  "products_discovered",
  "elapsed_seconds",
];

/** The numeric counters of one run: its terminal summary, or the live progress mid-run. */
function counters(r: Run | undefined): Record<string, number> {
  const raw = (r?.summary ?? r?.progress ?? {}) as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw)) {
    // Numbers only: the same JSONB carries `stage`, `degraded_reason`, `error` and the
    // nested merchant `attempts{}` map, none of which subtract.
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

function fmtCounter(v: number): string {
  return Number.isInteger(v) ? fmtInt(v) : v.toFixed(2);
}

// The flow-agnostic comparison: every counter either run reported, and B - A.
//
// Reads the run rows the page already has rather than an endpoint, so it works for a flow
// nobody taught this page about. A counter present in one run and missing from the other
// reads as 0 on the missing side -- which is the truth for a run that never emitted it
// (a flow gained the counter, or an attempt died before its first flush).
function CountersTable({
  a,
  b,
  labelA,
  labelB,
}: {
  a: Run | undefined;
  b: Run | undefined;
  labelA: string;
  labelB: string;
}) {
  const ca = counters(a);
  const cb = counters(b);
  const keys = [...new Set([...Object.keys(ca), ...Object.keys(cb)])].sort((x, y) => {
    const ix = COUNTER_ORDER.indexOf(x);
    const iy = COUNTER_ORDER.indexOf(y);
    if (ix !== iy) return (ix < 0 ? Number.MAX_SAFE_INTEGER : ix) - (iy < 0 ? Number.MAX_SAFE_INTEGER : iy);
    return x.localeCompare(y);
  });
  if (keys.length === 0) {
    return (
      <Panel>
        <Panel.Body>
          <EmptyState compact title="Neither run recorded any counters" />
        </Panel.Body>
      </Panel>
    );
  }
  return (
    <Panel>
      <Panel.Head title="Counters" />
      <Panel.Body tight>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Counter</th>
                <th style={{ textAlign: "right" }} title={labelA}>
                  A
                </th>
                <th style={{ textAlign: "right" }} title={labelB}>
                  B
                </th>
                <th style={{ textAlign: "right" }}>Δ</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const av = ca[k] ?? 0;
                const bv = cb[k] ?? 0;
                return (
                  <tr key={k}>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {k}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {k in ca ? fmtCounter(av) : <span className="faint">—</span>}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {k in cb ? fmtCounter(bv) : <span className="faint">—</span>}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Delta
                        d={{
                          abs: bv - av,
                          // Percent needs a baseline; against zero there isn't one, and a
                          // made-up 100% would read as a measured change.
                          pct: av ? Math.round((1000 * (bv - av)) / av) / 10 : null,
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="muted" style={{ fontSize: 11, padding: "8px 4px 2px", lineHeight: 1.5 }}>
          A = {labelA} · B = {labelB}. Each run&apos;s terminal summary (its live progress while
          still running), exactly as the run monitor reads it. A dash means that run never
          reported the counter at all, which is not the same as reporting zero.
        </div>
      </Panel.Body>
    </Panel>
  );
}

function Delta({ d, pct }: { d: StatDelta | null; pct?: boolean }) {
  if (d == null) return <span className="faint num">—</span>;
  if (d.abs === 0) return <span className="faint num">—</span>;
  const up = d.abs > 0;
  const color = up ? "var(--ok)" : "var(--crit)";
  const val = pct ? `${up ? "+" : ""}${d.abs}%` : `${up ? "+" : ""}${fmtInt(d.abs)}`;
  return (
    <span className="num" style={{ color }}>
      {up ? "▲" : "▼"} {val}
      {d.pct != null && !pct ? (
        <span className="faint" style={{ fontSize: 10 }}>
          {" "}
          ({d.pct > 0 ? "+" : ""}
          {d.pct}%)
        </span>
      ) : null}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <span className="eyebrow">{label}</span>
      {children}
    </Stack>
  );
}

function RunPicker({
  runs,
  value,
  onChange,
}: {
  runs: Run[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select
      className="mono"
      style={{
        width: "auto",
        minWidth: 240,
        padding: "7px 10px",
        fontSize: 11.5,
      }}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    >
      {runs.length === 0 && <option value="">no runs</option>}
      {runs.map((r) => {
        const tone = statusTone(r.status);
        const mark = tone === "ok" ? "✓" : tone === "crit" ? "✗" : "·";
        return (
          <option key={r.id} value={r.id}>
            {mark} {runLabel(r)}
          </option>
        );
      })}
    </Select>
  );
}
